import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Reveal } from "../components/Reveal";
import { WORLDS, IMG } from "../data/worlds";

const pages = WORLDS[0].pages;

const PageCard = ({ page, i, progress, reduce }) => {
  const start = i * 0.22;
  const last = i === pages.length - 1;
  const y = useTransform(progress, [start, start + 0.16, start + 0.24, start + 0.42], ["85vh", "0vh", "0vh", last ? "0vh" : "-4vh"]);
  const rotate = useTransform(progress, [start, start + 0.16], [i % 2 ? 5 : -5, i % 2 ? 1.4 : -1.4]);
  const scale = useTransform(progress, [start + 0.24, start + 0.42], [1, last ? 1 : 0.94]);

  return (
    <motion.div
      style={reduce ? { zIndex: i + 1 } : { y, rotate, scale, zIndex: i + 1 }}
      className="absolute inset-0 flex items-center justify-center px-4 sm:px-8"
    >
      <div className="relative w-full max-w-[840px] rounded-[16px] sm:rounded-[22px] overflow-hidden bg-white" style={{ boxShadow: "0 32px 80px rgba(44,43,40,0.28)" }}>
        <img src={page.img} alt={`Original-Buchseite ${page.color} aus Kleine Welt, große Farben`} className="w-full block" />
      </div>
    </motion.div>
  );
};

const Caption = ({ page, i, progress }) => {
  const start = i * 0.22;
  const opacity = useTransform(progress, [start + 0.08, start + 0.16, start + 0.21, start + 0.26], [0, 1, 1, i === pages.length - 1 ? 1 : 0]);
  return (
    <motion.p
      style={{ opacity, color: page.hex }}
      className="absolute font-display italic text-3xl sm:text-4xl"
    >
      {page.word}
    </motion.p>
  );
};

export const BookScroll = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const bg = useTransform(
    scrollYProgress,
    [0, 0.2, 0.42, 0.64, 0.86],
    ["#FBF8F3", pages[0].soft, pages[1].soft, pages[2].soft, pages[3].soft]
  );

  return (
    <section data-testid="book-scroll-section">
      <div className="max-w-3xl mx-auto px-5 text-center pt-24 sm:pt-28 pb-4">
        <Reveal>
          <p className="eyebrow-label mb-5">Ein Blick ins Buch</p>
          <h2 className="font-display font-medium tracking-tight text-4xl sm:text-5xl leading-[1.08]">
            Blättere durch eine kleine <em style={{ color: "var(--terra)" }}>Farbenwelt.</em>
          </h2>
        </Reveal>
      </div>

      <motion.div ref={ref} className="relative" style={{ height: "420vh", background: reduce ? "var(--paper)" : bg }}>
        <div className="sticky top-0 h-screen overflow-hidden">
          <div className="relative h-full max-w-6xl mx-auto">
            <div className="absolute inset-0">
              {pages.map((p, i) => (
                <PageCard key={p.color} page={p} i={i} progress={scrollYProgress} reduce={reduce} />
              ))}
            </div>
            <div className="absolute inset-x-0 bottom-[6%] z-10 flex justify-center pointer-events-none">
              {pages.map((p, i) => (
                <Caption key={p.color} page={p} i={i} progress={scrollYProgress} />
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      <div className="max-w-4xl mx-auto px-5 text-center py-24">
        <Reveal>
          <p className="font-display text-2xl sm:text-3xl leading-snug mb-14">
            Weniger Ablenkung.<br />
            <em style={{ color: "var(--terra)" }}>Mehr Raum zum gemeinsamen Entdecken.</em>
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div
            className="relative max-w-[760px] mx-auto rounded-[16px] sm:rounded-[22px] overflow-hidden rotate-[1deg]"
            style={{ boxShadow: "0 28px 70px rgba(44,43,40,0.2)" }}
          >
            <img
              src={IMG.abschluss}
              alt="Abschlussseite: alle Farben und Tiere aus Kleine Welt, große Farben"
              className="w-full block"
              data-testid="book-overview-page"
            />
          </div>
          <p className="font-display italic text-lg sm:text-xl text-[color:var(--ink-muted)] mt-9">
            Und am Ende kommen alle Farben zusammen.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
